import { Pays } from "../../../interfaces-base";
import { fetchAddressById } from "../../../service/address.api";
import { AddressType, BaseForm, CustomerType, ProForm } from "../fa-interfaces";

const IDS_DISABLED_NUM_TVA = [1, 25, 27, 28, 29, 30, 32, 55];
const IDS_NOT_REQUIRED_NUM_TVA = [22, 23, 24, 26];

export function getDefaultBaseForm(): BaseForm {
  return {
    id: undefined,
    libelle: undefined,
    nom: undefined,
    prenom: undefined,
    ville: undefined,
    adresse: undefined,
    adresse2: undefined,
    code_postal: undefined,
    telephone_fixe: undefined,
    telephone_mobile: undefined,
    telephone_code_pays: 'FR',
    is_livraison: 1,
    is_facturation: 1,
    is_favorite: undefined,
    pays_id: 1
  };
}

export function getDefaultProForm(): ProForm {
  return {
    societe: null,
    categorie_juridique: null,
    code_ape: null,
    siret: null,
    numero_tva: null
  };
}

export async function getAddressFromId(id: number): Promise<[BaseForm, ProForm]> {
  let baseForm = getDefaultBaseForm();
  let proForm = getDefaultProForm();
  try {
    const response = await fetchAddressById(id);
    const { address } = response;
    baseForm = {
      id: address.id,
      libelle: address.libelle,
      nom: address.nom,
      prenom: address.prenom,
      ville: address.ville,
      adresse: address.adresse,
      adresse2: address.adresse2,
      code_postal: address.code_postal,
      telephone_fixe: address.telephone_fixe,
      telephone_mobile: address.telephone_mobile,
      telephone_code_pays: address.pays.codePays,
      is_livraison: address.is_livraison,
      is_facturation: address.is_facturation,
      pays_id: address.pays_id,
      is_favorite: address.is_favorite
    };
    if (address.societe && address.societe !== null) {
      proForm = {
        societe: address.societe,
        categorie_juridique: address.categorie_juridique ?? null,
        code_ape: address.code_ape ?? null,
        siret: address.siret ?? null,
        numero_tva: address.numero_tva ?? null
      };
    }
  } catch (e) {
    console.error(e);
  }
  return [baseForm, proForm];
}

export function countryHasNumTvaIntra(countryId: number): boolean {
  return !IDS_DISABLED_NUM_TVA.includes(countryId);
}

export function countryHasRequiredNumTvaIntra(countryId: number, type: AddressType): boolean {
  return countryHasNumTvaIntra(countryId) && !IDS_NOT_REQUIRED_NUM_TVA.includes(countryId) && type === 'shipping';
}

export function countryNeedSiret(country: Pays, zipCode?: string): boolean {
  const regexMonaco = /^98/;
  return (country.codePays === 'FR' || country.codePaysTva === 'FR') && !regexMonaco.test(zipCode ?? '');
}

export function countryHasRequiredSiret(country: Pays, type: AddressType, zipCode?: string): boolean {
  return countryNeedSiret(country, zipCode) && type === 'billing';
}

export function getCountryFlagUrl(country: Pays): string {
  let isoCode = country.codePays;
  if (['GP', 'GF', 'PM', 'RE', 'MQ', 'BL', 'MF', 'YT'].includes(isoCode)) {
    isoCode = 'FR';
  }
  return `/order/images/flags/${isoCode}.png`
}

export function clearNumTva(numTva: string, country: Pays): string {
  return numTva.replace(/[ .-]/g, '')
    .replace(country.codePaysTva.toUpperCase(), '')
    .replace(country.codePaysTva.toLowerCase(), '');
}

export function formatNumTva(numTva: string, country: Pays): string {
  const numTvaCleared = clearNumTva(numTva, country);
  return country.codePaysTva.concat(numTvaCleared);
}

export function findCountry(list: Pays[], id: number): Pays {
  const defaultCountry: Pays = { idPays: 1, codePays: 'FR', codePaysTva: 'FR', Europe: 1, inactif: 0, Libelle: '', livraisons_pro_uniquement: 0, taux_tva: '20', phone_international_code: 33 };
  return list.find(ct => ct.idPays === id) ?? defaultCountry;
}

export function getSubmitState(customerType: CustomerType, baseForm: BaseForm, proForm: ProForm): BaseForm & ProForm {
  let result = { ...baseForm, ...getDefaultProForm() };
    if (customerType === 'pro') {
      result = { ...baseForm, ...proForm };
    }
    return result;
}

export function cleanWhitespace(string: string): string {
  return string.replace(/[ ]+/g, '');
}

export function prettifySiret(siret: string): string {
  return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4');
}

export function countryIsAvailableForShipping(country: Pays, isPro: boolean): boolean {
  return country.inactif === 0 && (isPro || country.livraisons_pro_uniquement === 0);
}
